import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlatformRolePicker } from "@/components/pages/users/components/PlatformRolePicker";
import { useCustomRoles } from "@/components/pages/users/hooks/useCustomRoles";
import { useCreateGuesteriaAdmin } from "@/components/pages/users/hooks/useGuesteriaAdmins";
import { validateEmail } from "@/utils/validation";
import type { CreateGuesteriaAdminDto } from "@/types/guesteria-admin";

const NAME_MAX_LENGTH = 64;

interface SuperAdminUserCreatePageProps {
  backTo: string;
}

const PASSWORD_MIN_LENGTH = 8;

const createPayload = ({
  email,
  firstName,
  lastName,
  customRoleId,
  password,
}: {
  email: string;
  firstName: string;
  lastName: string;
  customRoleId: string | null;
  password: string;
}): CreateGuesteriaAdminDto => ({
  username: email.trim(),
  email: email.trim(),
  ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
  ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
  ...(customRoleId ? { customRoleId } : {}),
  ...(password.trim() ? { password: password.trim() } : {}),
});

export const SuperAdminUserCreatePage: React.FC<
  SuperAdminUserCreatePageProps
> = ({ backTo }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selectedCustomRoleId, setSelectedCustomRoleId] = useState<
    string | null
  >(null);
  const { data: platformRolesResponse, isLoading: isPlatformRolesLoading } =
    useCustomRoles("platform");
  const createGuesteriaAdminMutation = useCreateGuesteriaAdmin();

  const platformRoles = platformRolesResponse ?? [];

  const onBack = () => {
    navigate(backTo);
  };

  const onSave = async () => {
    const trimmedEmail = email.trim();

    if (!validateEmail(trimmedEmail).isValid) {
      toast.error(t("users.form.emailRequired", "Valid email is required"));
      return;
    }

    const trimmedPassword = password.trim();
    if (
      trimmedPassword.length > 0 &&
      trimmedPassword.length < PASSWORD_MIN_LENGTH
    ) {
      toast.error(t("users.form.passwordMinLength"));
      return;
    }

    try {
      await createGuesteriaAdminMutation.mutateAsync(
        createPayload({
          email,
          firstName,
          lastName,
          customRoleId: selectedCustomRoleId,
          password,
        }),
      );
      toast.success(
        t(
          "users.form.guesteriaAdminCreatedSuccessfully",
          "Guesteria admin created successfully",
        ),
      );
      navigate(backTo);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t(
              "users.form.failedToCreateGuesteriaAdmin",
              "Failed to create Guesteria admin",
            );
      toast.error(message);
    }
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-foreground h-8 rounded-md bg-transparent px-3 py-1 text-base font-normal hover:bg-transparent"
        >
          <Undo2 className="h-6 w-6" />
          {t("users.page.back")}
        </Button>
      </div>

      <div className="bg-background flex flex-col gap-6 rounded-lg p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            label={t("users.form.email")}
            placeholder={t("users.form.email", "Email")}
            type="email"
          />
          <div className="flex w-full flex-col gap-1.5">
            <label
              htmlFor="guesteria-admin-password"
              className="text-sm leading-none font-medium text-zinc-900"
            >
              {t("users.form.password")}
            </label>
            <div className="relative w-full">
              <Input
                id="guesteria-admin-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={t("users.form.password", "Password")}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-1/2 right-3 h-8 w-8 -translate-y-1/2 text-black/45 hover:bg-transparent hover:text-black/45"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword
                    ? t("users.form.hidePassword", "Hide password")
                    : t("users.form.showPassword", "Show password")
                }
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-sm leading-5 text-black/45">
              {t("users.form.passwordOptionalInvite")}
            </p>
          </div>
          <Input
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            label={t("users.form.firstName")}
            placeholder={t("users.form.firstName", "First Name")}
            maxLength={NAME_MAX_LENGTH}
          />
          <Input
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            label={t("users.form.lastName")}
            placeholder={t("users.form.lastName", "Last Name")}
            maxLength={NAME_MAX_LENGTH}
          />
        </div>

        <PlatformRolePicker
          roles={platformRoles}
          selectedRoleId={selectedCustomRoleId}
          isLoading={isPlatformRolesLoading}
          label={t("users.groups.create.platformRolesLabel")}
          description={t("users.form.platformRolesDescription")}
          onSelectedRoleIdChange={setSelectedCustomRoleId}
        />

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onBack} className="h-8 px-4">
            {t("users.form.cancel", "Cancel")}
          </Button>
          <Button
            variant="default"
            className="h-8 px-4"
            onClick={() => void onSave()}
            disabled={createGuesteriaAdminMutation.isPending}
          >
            {createGuesteriaAdminMutation.isPending
              ? t("users.form.creating", "Creating...")
              : t("users.form.createUser")}
          </Button>
        </div>
      </div>
    </div>
  );
};
